import { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Store } from '@/shared/store';
import { getOrganizationMembersAction } from '@/shared/store/organization/actions';
import { Member, MemberStatus } from '@/shared/api/organization';

export const STATUS = {
    ...MemberStatus,
    ALL: 'all',
};

export type STATUS = (typeof STATUS)[keyof typeof STATUS];

export const useTeamSection = () => {
    const dispatch = useDispatch();
    const { organizationMembers, organizationMembersLoading, organizationId } = useSelector(
        (state: Store) => ({
            organizationMembers: state.organization.organizationMembers,
            organizationMembersLoading: state.organization.organizationMembersLoading,
            organizationId: state.organization.info.organization.id,
        }),
    );

    const fetchOrganizationMembers = useCallback(() => {
        if (organizationId > 0 && !organizationMembersLoading) {
            dispatch(getOrganizationMembersAction(organizationId));
        }
    }, [organizationId]);

    useEffect(() => {
        fetchOrganizationMembers();
    }, [fetchOrganizationMembers]);

    const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<STATUS>(STATUS.ALL);

    const filteredMembers = useMemo(() => {
        return organizationMembers.filter((member: Member) => {
            const matchesName = member.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesEmail = member.email.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus =
                statusFilter === STATUS.ALL ||
                (member.status !== undefined && member.status === statusFilter);
            return (matchesName || matchesEmail) && matchesStatus;
        });
    }, [organizationMembers, searchTerm, statusFilter]);

    const emailsList = useMemo(() => {
        return organizationMembers?.map((member: Member) => member.email);
    }, [organizationMembers]);

    return {
        emailsList,
        organizationMembersLoading,
        fetchOrganizationMembers,
        filteredMembers,
        selectedMembers,
        setSelectedMembers,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        organizationMembers,
    };
};
